import { api } from './axios';

/**
 * Валидация ID
 * @param {string|number} id - Идентификатор
 * @param {string} name - Название поля для ошибки
 */
const validateId = (id, name = 'ID') => {
    if (!id && id !== 0) throw new Error(`${name} is required`);
};

/**
 * API для работы с профилями
 */
export const profilesApi = {
    /**
     * Получает список всех публичных профилей с пагинацией
     * @param {Object} [params] - Параметры запроса
     * @param {number} [params.page=0] - Номер страницы (0-indexed)
     * @param {number} [params.size=20] - Размер страницы
     * @param {string} [params.sortBy] - Поле для сортировки
     * @param {string} [params.direction=ASC] - Направление сортировки (ASC, DESC)
     * @param {string} [params.search] - Поисковый запрос
     * @param {string} [params.profileType] - Фильтр по типу профиля
     * @param {string} [params.country] - Фильтр по стране
     * @param {string} [params.city] - Фильтр по городу
     * @returns {Promise<{content: Array, page: {size: number, number: number, totalElements: number, totalPages: number}}>}
     * @example
     * // Получить первую страницу (20 профилей)
     * const response = await profilesApi.list();
     * console.log(response.content); // массив профилей
     * console.log(response.page);    // информация о пагинации
     *
     * // Получить все профили (до 1000)
     * const response = await profilesApi.list({ size: 1000 });
     * const profiles = response.content;
     */
    list: async (params = {}) => {
        try {
            const { data } = await api.get('/profiles', { params });
            return data;
        } catch (error) {
            console.error('[profilesApi.list] Failed to fetch profiles:', error.message);
            throw error;
        }
    },

    /**
     * Получает список всех публичных профилей (legacy, без пагинации)
     * @deprecated Используйте list() с параметрами
     * @returns {Promise<Array>} Список профилей
     */
    listAll: async () => {
        try {
            const { data } = await api.get('/profiles', { params: { size: 1000 } });
            return data.content || data;
        } catch (error) {
            console.error('[profilesApi.listAll] Failed to fetch profiles:', error.message);
            throw error;
        }
    },

    /**
     * Получает профиль по ID
     * @param {string|number} id - ID профиля
     * @returns {Promise<Object>}
     */
    get: async (id) => {
        validateId(id, 'Profile ID');
        try {
            const { data } = await api.get(`/profiles/${id}`);
            return data;
        } catch (error) {
            console.error(`[profilesApi.get] Failed to fetch profile ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Получает профиль по ID пользователя
     * @param {string|number} userId - ID пользователя
     * @returns {Promise<Object>}
     */
    getByUserId: async (userId) => {
        validateId(userId, 'User ID');
        try {
            const { data } = await api.get(`/profiles/user/${userId}`);
            return data;
        } catch (error) {
            console.error(`[profilesApi.getByUserId] Failed to fetch profile for user ${userId}:`, error.message);
            throw error;
        }
    },

    /**
     * Обновляет профиль
     * @param {string|number} id - ID профиля
     * @param {Object} payload - Поля для обновления
     * @returns {Promise<Object>}
     */
    update: async (id, payload) => {
        validateId(id, 'Profile ID');
        if (!payload) throw new Error('Update payload is required');
        try {
            const { data } = await api.put(`/profiles/${id}`, payload);
            return data;
        } catch (error) {
            console.error(`[profilesApi.update] Failed to update profile ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Удаляет профиль
     * @param {string|number} id - ID профиля
     * @returns {Promise<Object>}
     */
    remove: async (id) => {
        validateId(id, 'Profile ID');
        try {
            const { data } = await api.delete(`/profiles/${id}`);
            return data;
        } catch (error) {
            console.error(`[profilesApi.remove] Failed to delete profile ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Загружает аватар профиля
     * @param {string|number} id - ID профиля
     * @param {FormData} formData - Данные формы с файлом
     * @param {Function} [onUploadProgress] - Коллбэк для отслеживания прогресса
     * @returns {Promise<Object>}
     */
    uploadAvatar: async (id, formData, onUploadProgress = null) => {
        validateId(id, 'Profile ID');
        if (!formData) throw new Error('FormData is required for avatar upload');

        try {
            // Сервер ожидает entityType=PROFILE и entityId как query-параметры
            const { data } = await api.post(`/profiles/${id}/avatar`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                params: { entityType: 'PROFILE', entityId: id },
                onUploadProgress
            });
            return data;
        } catch (error) {
            console.error(`[profilesApi.uploadAvatar] Failed to upload avatar for profile ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Получает аватар по ID файла
     * @param {string|number} fileId - ID файла аватара (UUID или путь /api/files/{uuid})
     * @returns {Promise<Blob>}
     */
    getAvatar: async (fileId) => {
        // Нормализуем: если передан путь /api/files/{uuid}, извлекаем UUID
        const normalizedId = typeof fileId === 'string' && fileId.startsWith('/api/files/')
            ? fileId.replace('/api/files/', '')
            : fileId;
        validateId(normalizedId, 'File ID');
        try {
            const { data } = await api.get(`/profiles/avatar/${normalizedId}`, { responseType: 'blob' });
            return data;
        } catch (error) {
            console.error(`[profilesApi.getAvatar] Failed to fetch avatar ${normalizedId}:`, error.message);
            throw error;
        }
    },

    /**
     * Удаляет аватар профиля
     * @param {string|number} id - ID профиля
     * @returns {Promise<Object>}
     */
    deleteAvatar: async (id) => {
        validateId(id, 'Profile ID');
        try {
            const { data } = await api.delete(`/profiles/${id}/avatar`);
            return data;
        } catch (error) {
            console.error(`[profilesApi.deleteAvatar] Failed to delete avatar for profile ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Создаёт новый публичный профиль
     * @param {Object} payload - PublicProfileRequest (userId, profileType, displayName, bio, country, city, socialLinks)
     * @returns {Promise<Object>}
     */
    create: async (payload) => {
        if (!payload) throw new Error('Profile payload is required');
        if (!payload.userId) throw new Error('userId is required');
        if (!payload.profileType) throw new Error('profileType is required');
        if (!payload.displayName) throw new Error('displayName is required');

        try {
            const { data } = await api.post('/profiles', payload);
            return data;
        } catch (error) {
            console.error('[profilesApi.create] Failed to create profile:', error.message);
            throw error;
        }
    },

    /**
     * Загружает обложку профиля
     * @param {string|number} id - ID профиля
     * @param {FormData} formData - Данные формы с файлом
     * @param {Function} [onUploadProgress] - Коллбэк для отслеживания прогресса
     * @returns {Promise<Object>}
     */
    uploadCover: async (id, formData, onUploadProgress = null) => {
        validateId(id, 'Profile ID');
        if (!formData) throw new Error('FormData is required for cover upload');

        try {
            const { data } = await api.post(`/profiles/${id}/cover`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress
            });
            return data;
        } catch (error) {
            console.error(`[profilesApi.uploadCover] Failed to upload cover for profile ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Получает обложку по ID файла
     * @param {string|number} fileId - ID файла обложки
     * @returns {Promise<Blob>}
     */
    getCover: async (fileId) => {
        validateId(fileId, 'File ID');
        try {
            const { data } = await api.get(`/profiles/cover/${fileId}`, { responseType: 'blob' });
            return data;
        } catch (error) {
            console.error(`[profilesApi.getCover] Failed to fetch cover ${fileId}:`, error.message);
            throw error;
        }
    },

    /**
     * Удаляет обложку профиля
     * Endpoint: DELETE /api/profiles/{id}/cover
     * @param {string|number} id - ID профиля
     * @returns {Promise<Object>}
     */
    deleteCover: async (id) => {
        validateId(id, 'Profile ID');
        try {
            const { data } = await api.delete(`/profiles/${id}/cover`);
            return data;
        } catch (error) {
            console.error(`[profilesApi.deleteCover] Failed to delete cover for profile ${id}:`, error.message);
            throw error;
        }
    }
}